import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { usePageTitle, useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";

export default function FamilyPage() {
  const { slug } = useParams();
  const { meta, products, loading } = useShop();
  const family = (meta.families || []).find((f) => f.slug === slug);
  usePageTitle(family ? `${family.name} Hijabs` : "Shop by Colour");
  const [heroOk, setHeroOk] = useState(true);

  const items = useMemo(
    () => products.filter((p) => p.type === "single" && family && p.colour_family === family.name),
    [products, family]
  );

  const byCollection = useMemo(() => {
    const order = (meta.collections || []).map((c) => c.name);
    const groups = {};
    items.forEach((p) => {
      (groups[p.collection] = groups[p.collection] || []).push(p);
    });
    return order
      .filter((c) => groups[c]?.length)
      .map((c) => ({ name: c, fabric: groups[c][0].fabric, items: groups[c] }));
  }, [items, meta.collections]);

  if (!loading && !family) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-32 text-center" data-testid="family-not-found">
        <p className="font-serif text-3xl">Colour family not found</p>
        <Link to="/collections" className="btn-primary mt-8">Browse Collections</Link>
      </div>
    );
  }
  if (!family) return null;

  return (
    <div data-testid={`family-page-${slug}`}>
      <section className="relative min-h-[300px] h-[44vh] overflow-hidden" style={{ backgroundColor: family.hex }}>
        {heroOk && (
          <img
            src={`/images/families/${slug}.jpg`}
            alt={`The LAMSAH ${family.name} colour family`}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setHeroOk(false)}
          />
        )}
        <div className="absolute inset-0 bg-espresso/40" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-8 flex items-end pb-14">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-ivory/80 mb-4">
              Shop by Colour · {items.length} Shades
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-ivory" data-testid="family-title">
              {family.name.toUpperCase()}
            </h1>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20">
        <Reveal>
          <p className="text-sm sm:text-base text-espresso/75 leading-relaxed max-w-2xl" data-testid="family-story">
            {family.story}
          </p>
          <div className="flex flex-wrap gap-2 mt-8">
            {(meta.families || []).filter((f) => f.slug !== slug).map((f) => (
              <Link
                key={f.slug}
                to={`/colours/${f.slug}`}
                data-testid={`family-switch-${f.slug}`}
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] px-3.5 py-2 border border-beige text-taupe hover:border-taupe hover:text-espresso transition-colors duration-300"
              >
                <span className="w-3 h-3 rounded-full border border-beige" style={{ backgroundColor: f.hex }} />
                {f.name}
              </Link>
            ))}
          </div>
          <Link
            to={`/shop?family=${encodeURIComponent(family.name)}`}
            data-testid="family-shop-all"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-rose border-b border-rose/40 pb-0.5 mt-8 hover:text-espresso transition-colors"
          >
            Shop all {family.name} with filters
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </Reveal>

        {byCollection.map((group) => (
          <div key={group.name} className="mt-16 md:mt-20" data-testid={`family-collection-${group.name.toLowerCase()}`}>
            <div className="flex items-baseline justify-between gap-4 mb-8">
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl font-light tracking-tight">{group.name}</h2>
                <p className="text-xs uppercase tracking-[0.2em] text-taupe mt-1.5">{group.fabric}</p>
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-taupe">{group.items.length} {group.items.length === 1 ? "shade" : "shades"}</p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
              {group.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
